import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { LOOK_WORDS, lookContent, lookTitle, type LibraryLook } from "../lib/exerciseLook";
import { restLine } from "../lib/weekView";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * W137 R3b point 3 — ONE EXERCISE, TO BE SEEN (Fable's ruling, 2026-10-02, on his "Only if we click the exercise text it can
 * open to see it"). Opened by an exercise's NAME in the Extended column and nowhere else. A native `<dialog>` on the app's
 * window mechanism (`ht-window`): Close, Escape and a click on the dimmed ground close it; the caller returns focus.
 *
 * ⚠️ IT WRITES NOTHING — a source ruler holds that (no insert/update/delete/rpc/upsert in this file). The ONE read is the
 * library row (video, steps, notes) when it opens; the prescription comes from what the Week already holds. `Edit sets`
 * closes it and opens the per-day set editor — the old door, one click away.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */
export function ExerciseLookWindow({ exerciseId, label, name, dose, rest, note, onClose, onEditSets }: {
  /** The LIBRARY exercise (`coach_exercises.id`); null = the row names none, so there is nothing to read. */
  exerciseId: string | null;
  label: string | null;
  name: string;
  /** This day's prescription as the column prints it. */
  dose: string;
  rest: string | null;
  note: string | null;
  onClose: () => void;
  /** Absent = no `Edit sets` (a surface with no editor). */
  onEditSets?: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [row, setRow] = useState<LibraryLook | null | undefined>(undefined);
  useEffect(() => {
    const el = ref.current;
    if (el !== null && !el.open) el.showModal();
  }, []);
  useEffect(() => {
    let cancelled = false;
    if (exerciseId === null) { setRow(null); return; }
    void (async () => {
      const { data, error } = await supabase.from("coach_exercises")
        .select("demo_video_url, instructions, notes").eq("id", exerciseId).limit(1);
      if (cancelled) return;
      const first = (data ?? [])[0] as LibraryLook | undefined;
      setRow(error !== null || first === undefined ? null : first);
    })();
    return () => { cancelled = true; };
  }, [exerciseId]);
  const content = lookContent(row);
  const title = lookTitle(label, name);
  return (
    <dialog ref={ref} className="ht-window exercise-look-window" aria-labelledby="exercise-look-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ht-window-frame">
        <header className="ht-window-head">
          <div className="ht-window-titles">
            <h2 className="ht-title" id="exercise-look-title">{title}</h2>
          </div>
          <button type="button" className="ht-button ht-button-quiet ht-window-close" onClick={onClose}>Close</button>
        </header>
        <div className="ht-window-body exercise-look-body">
          {content.kind === "loading" && <p className="ht-caption" aria-live="polite">Loading…</p>}
          {content.kind === "failed" && <p className="ht-callout" role="alert">{LOOK_WORDS.failed}</p>}
          {content.kind === "ready" && (
            content.video.kind === "file" ? (
              /* The library editor's own player for an uploaded file — one player, not a new one. */
              <video src={content.video.src} controls playsInline className="xb-video-player exercise-look-video" />
            ) : content.video.kind === "link" ? (
              <p className="exercise-look-link">
                <a className="ht-link" href={content.video.href} target="_blank" rel="noreferrer noopener">{LOOK_WORDS.openLink}</a>
                <span className="ht-caption"> {content.video.host}</span>
              </p>
            ) : (
              <p className="ht-caption exercise-look-novideo">{LOOK_WORDS.noVideo}</p>
            )
          )}
          {/* THIS DAY'S prescription: the dose, the rest, the coach's note in full (never clamped here). */}
          <section className="exercise-look-dose" aria-label="This day">
            <p className="exercise-look-line">{dose}</p>
            {restLine(rest) !== null && <p className="ht-caption exercise-look-rest">{restLine(rest)}</p>}
            {note !== null && <p className="exercise-look-note">{note}</p>}
          </section>
          {content.kind === "ready" && content.steps.length > 0 && (
            <section className="exercise-look-steps" aria-labelledby="exercise-look-howto">
              <h3 className="ht-overline" id="exercise-look-howto">{LOOK_WORDS.howTo}</h3>
              <ol>{content.steps.map((s, i) => <li key={`${String(i)}-${s.slice(0, 12)}`}>{s}</li>)}</ol>
            </section>
          )}
          {content.kind === "ready" && content.notes !== null && <p className="exercise-look-libnote">{content.notes}</p>}
        </div>
        {onEditSets !== undefined && (
          <footer className="ht-window-foot exercise-look-foot">
            <button type="button" className="ours-text-action" onClick={onEditSets}>{LOOK_WORDS.editSets}</button>
          </footer>
        )}
      </div>
    </dialog>
  );
}
